import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wrench, Sun, Flame, MessageSquare, CalendarClock, AlertCircle,
  ArrowRight, ChevronRight, User as UserIcon, Sparkles, Building2,
} from "lucide-react";
import {
  collectProjectTermine, terminCategory, CATEGORY_META,
} from "@/pages/Projekttermine";

// Lokales Datum als YYYY-MM-DD — kein toISOString (UTC-Shift-Bug).
function fmtLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBetween(a, b) {
  const ms = new Date(b).setHours(0, 0, 0, 0) - new Date(a).setHours(0, 0, 0, 0);
  return Math.round(ms / 86_400_000);
}

// Eine "offene Aufgabe" = Phase, die noch nicht abgenommen ist
const OPEN_PHASE_STATES = new Set(["offen", "geplant", "in_arbeit", "fertig"]);

function countOpenItems(project) {
  let n = 0;
  for (const ph of project.phases || []) {
    if (OPEN_PHASE_STATES.has(ph.status)) n += 1;
  }
  return n;
}

function projectProgress(project) {
  const phases = project.phases || [];
  if (!phases.length) return { done: 0, total: 0, pct: 0 };
  const done = phases.filter((p) => p.status === "abgenommen").length;
  return { done, total: phases.length, pct: Math.round((done / phases.length) * 100) };
}

function nextTerminOfProject(project, todayStr) {
  const upcoming = (project.phases || [])
    .filter((p) => p.termin && p.termin >= todayStr)
    .sort((a, b) => a.termin.localeCompare(b.termin));
  return upcoming[0] || null;
}

function KpiCard({ label, value, icon: Icon, tint, to, testid, hint }) {
  const Body = (
    <Card className="border-border hover:border-primary/60 hover:shadow-sm transition-all cursor-pointer h-full">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl lg:text-4xl font-bold mt-2 tracking-tight" data-testid={`${testid}-value`}>{value ?? 0}</div>
            {hint && <div className="text-[11px] text-muted-foreground mt-1 truncate">{hint}</div>}
          </div>
          <Icon className={`h-6 w-6 ${tint} shrink-0`} />
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} data-testid={testid}>{Body}</Link> : <div data-testid={testid}>{Body}</div>;
}

export default function MeinDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAuftraggeber = user?.role === "auftraggeber";
  const isStaff = ["admin", "innendienst"].includes(user?.role);

  const [projects, setProjects] = useState([]);
  const [monteure, setMonteure] = useState([]);
  const [noteUnread, setNoteUnread] = useState({});      // { projectId: count }
  const [msgUnread, setMsgUnread] = useState(0);         // Direktnachrichten
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [ps, ms, unread, msgU] = await Promise.all([
          api.get("/projects").then((x) => x.data).catch(() => []),
          isStaff ? api.get("/monteure").then((x) => x.data).catch(() => []) : Promise.resolve([]),
          api.get("/projects/notes/unread-counts").then((x) => x.data).catch(() => ({})),
          api.get("/messages/unread-count").then((x) => x.data).catch(() => ({ unread: 0 })),
        ]);
        if (!alive) return;
        setProjects(ps || []);
        setMonteure(ms || []);
        setNoteUnread(unread || {});
        setMsgUnread(msgU?.unread || 0);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isStaff]);

  const monteurName = (id) => monteure.find((m) => m.id === id)?.name || monteure.find((m) => m.id === id)?.email;

  const today = new Date();
  const todayStr = fmtLocal(today);
  const in7Days = new Date(today);
  in7Days.setDate(in7Days.getDate() + 7);
  const in7Str = fmtLocal(in7Days);

  // Alle Termine sammeln (aus Phases)
  const termine = useMemo(() => collectProjectTermine(projects), [projects]);

  const upcomingTermine = useMemo(
    () => termine.filter((t) => t.termin >= todayStr).slice(0, 8),
    [termine, todayStr]
  );

  const termineWithin7 = useMemo(
    () => termine.filter((t) => t.termin >= todayStr && t.termin <= in7Str),
    [termine, todayStr, in7Str]
  );

  // Aktive Projekte = Projekte mit mind. einer offenen Phase
  const activeProjects = useMemo(
    () => projects.filter((p) => countOpenItems(p) > 0),
    [projects]
  );

  // Offene Punkte gesamt
  const totalOpenItems = useMemo(
    () => projects.reduce((sum, p) => sum + countOpenItems(p), 0),
    [projects]
  );

  // Ungelesene Nachrichten gesamt (aus Projekt-Notizen)
  const totalNoteUnread = useMemo(
    () => Object.values(noteUnread).reduce((s, v) => s + (Number(v) || 0), 0),
    [noteUnread]
  );

  // Projekte sortiert: ungelesene zuerst, dann nach nächstem Termin
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const uA = noteUnread[a.id] || 0;
      const uB = noteUnread[b.id] || 0;
      if (uB !== uA) return uB - uA;
      const tA = nextTerminOfProject(a, todayStr)?.termin || "9999";
      const tB = nextTerminOfProject(b, todayStr)?.termin || "9999";
      return tA.localeCompare(tB);
    });
  }, [projects, noteUnread, todayStr]);

  // Offene Punkte als flache Liste (Top-8) — für Widget
  const openItemsList = useMemo(() => {
    const out = [];
    for (const p of projects) {
      for (const ph of p.phases || []) {
        if (OPEN_PHASE_STATES.has(ph.status)) {
          out.push({
            projectId: p.id,
            project_type: p.project_type,
            kunde_name: p.kunde_name,
            phase_key: ph.key,
            phase_label: ph.label,
            status: ph.status,
            termin: ph.termin,
          });
        }
      }
    }
    // Prio: mit Termin zuerst nach Datum, dann ohne Termin
    out.sort((a, b) => {
      if (!!a.termin !== !!b.termin) return a.termin ? -1 : 1;
      if (a.termin && b.termin) return a.termin.localeCompare(b.termin);
      return 0;
    });
    return out.slice(0, 8);
  }, [projects]);

  const openProject = (pid) => navigate(`/projekte?open=${pid}`);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return "Gute Nacht";
    if (h < 11) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
  }, []);

  return (
    <div className="p-4 lg:p-8 space-y-8 animate-fade-up" data-testid="mein-dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2">
            <Sparkles className="h-3 w-3 text-primary" />
            {isAuftraggeber ? "Kunden-Portal" : "Kunden-Cockpit"}
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2" data-testid="mein-dashboard-title">
            {greeting}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isAuftraggeber
              ? "Deine Projekte, Termine und Nachrichten auf einen Blick."
              : "Kunden-Sicht auf alle Projekte — Termine, offene Punkte und Nachrichten."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/projekte")} data-testid="mein-dashboard-open-projekte">
            <Wrench className="h-4 w-4 mr-2" /> Alle Projekte
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/projekttermine")} data-testid="mein-dashboard-open-termine">
            <CalendarClock className="h-4 w-4 mr-2" /> Voller Kalender
          </Button>
        </div>
      </div>

      {/* Neue Nachrichten — Alert ganz oben, nur wenn ungelesene vorhanden */}
      {totalNoteUnread > 0 && (
        <Card className="border-primary/60 bg-primary/5 shadow-sm" data-testid="widget-new-messages">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <span className="relative inline-flex">
                <MessageSquare className="h-5 w-5 text-primary" />
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-destructive animate-pulse" />
              </span>
              Neue Nachrichten
              <span className="ml-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold px-2 h-5 inline-flex items-center" data-testid="widget-new-messages-badge">
                {totalNoteUnread}
              </span>
            </CardTitle>
            <Link to="/nachrichten" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="widget-new-messages-chat-link">
              Direkt-Chat <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-primary/10">
              {sortedProjects
                .filter((p) => (noteUnread[p.id] || 0) > 0)
                .slice(0, 5)
                .map((p) => {
                  const n = noteUnread[p.id] || 0;
                  return (
                    <li key={`nm-${p.id}`}>
                      <button
                        onClick={() => openProject(p.id)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-primary/5 text-left"
                        data-testid={`new-message-item-${p.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {p.project_type === "pv" ? (
                            <Sun className="h-4 w-4 text-primary shrink-0" />
                          ) : (
                            <Flame className="h-4 w-4 text-orange-500 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{p.kunde_name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {n} ungelesene Nachricht{n === 1 ? "" : "en"} · zum Projekt springen
                            </div>
                          </div>
                        </div>
                        <span className="rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold px-2 min-w-[1.5rem] h-6 inline-flex items-center justify-center shrink-0 ml-3" data-testid={`new-message-count-${p.id}`}>
                          {n}
                        </span>
                      </button>
                    </li>
                  );
                })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-active-projects"
          label="Aktive Projekte"
          value={activeProjects.length}
          icon={Wrench}
          tint="text-primary"
          hint={`${projects.length} gesamt`}
          to="/projekte"
        />
        <KpiCard
          testid="kpi-unread-messages"
          label="Ungelesene Nachrichten"
          value={totalNoteUnread + msgUnread}
          icon={MessageSquare}
          tint="text-red-500"
          hint={
            totalNoteUnread + msgUnread > 0
              ? `${totalNoteUnread} Projekt · ${msgUnread} Direkt`
              : "Alles gelesen"
          }
          to="/nachrichten"
        />
        <KpiCard
          testid="kpi-termine-7d"
          label="Termine · 7 Tage"
          value={termineWithin7.length}
          icon={CalendarClock}
          tint="text-sky-500"
          hint={termineWithin7.length === 0 ? "Keine anstehenden Termine" : undefined}
          to="/projekttermine"
        />
        <KpiCard
          testid="kpi-open-items"
          label="Offene Punkte"
          value={totalOpenItems}
          icon={AlertCircle}
          tint="text-amber-500"
          hint={totalOpenItems === 0 ? "Alle Phasen abgeschlossen" : `in ${activeProjects.length} Projekten`}
        />
      </div>

      {/* Zwei-Spalten: Nächste Termine + Ungelesene */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Nächste Termine */}
        <Card className="border-border" data-testid="widget-upcoming">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-primary" /> Nächste Termine
            </CardTitle>
            <Link to="/projekttermine" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="widget-upcoming-link">
              Kalender <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingTermine.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center" data-testid="widget-upcoming-empty">
                Keine anstehenden Termine.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {upcomingTermine.map((t, i) => {
                  const cat = terminCategory(t);
                  const meta = CATEGORY_META[cat];
                  const dOff = daysBetween(new Date(), t.termin);
                  const relLbl =
                    dOff === 0 ? "Heute" : dOff === 1 ? "Morgen" : dOff < 0 ? `vor ${-dOff} T` : `in ${dOff} T`;
                  return (
                    <li key={`u-${t.projectId}-${t.phase_key}-${i}`}>
                      <button
                        onClick={() => openProject(t.projectId)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary/60 text-left"
                        data-testid={`upcoming-item-${t.projectId}-${t.phase_key}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${meta.color}`} />
                          {t.project_type === "pv" ? (
                            <Sun className="h-4 w-4 text-primary shrink-0" />
                          ) : (
                            <Flame className="h-4 w-4 text-purple-500 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{t.kunde_name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {t.phase_label}
                              {t.monteur_id && isStaff && (
                                <> · <span className="text-primary inline-flex items-center gap-0.5"><UserIcon className="h-3 w-3" />{monteurName(t.monteur_id) || "Monteur"}</span></>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="text-xs font-medium">{relLbl}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{t.termin}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Offene Punkte */}
        <Card className="border-border" data-testid="widget-open-items">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> Offene Punkte
            </CardTitle>
            <Link to="/projekte" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="widget-open-link">
              Alle Projekte <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {openItemsList.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center" data-testid="widget-open-empty">
                🎉 Alle Phasen abgeschlossen.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {openItemsList.map((it, i) => (
                  <li key={`o-${it.projectId}-${it.phase_key}-${i}`}>
                    <button
                      onClick={() => openProject(it.projectId)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary/60 text-left"
                      data-testid={`open-item-${it.projectId}-${it.phase_key}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {it.project_type === "pv" ? (
                          <Sun className="h-4 w-4 text-primary shrink-0" />
                        ) : (
                          <Flame className="h-4 w-4 text-purple-500 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{it.kunde_name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {it.phase_label} · <span className="capitalize">{it.status.replace("_", " ")}</span>
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 ml-3">
                        {it.termin ? (
                          <span className="font-mono text-[10px] text-muted-foreground">{it.termin}</span>
                        ) : (
                          <span className="text-[10px] text-amber-600 uppercase tracking-wider">ohne Termin</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Meine Projekte */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight" data-testid="widget-projects-title">
              {isAuftraggeber ? "Meine Projekte" : "Projekte"}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {loading ? "Wird geladen…" : `${projects.length} Projekt${projects.length === 1 ? "" : "e"}`}
            </p>
          </div>
          <Link to="/projekte" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="widget-projects-link">
            Alle öffnen <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {!loading && projects.length === 0 ? (
          <Card className="border-dashed border-border" data-testid="widget-projects-empty">
            <CardContent className="p-10 text-center">
              <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">Noch keine Projekte hinterlegt.</div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedProjects.slice(0, 9).map((p) => {
              const prog = projectProgress(p);
              const openN = countOpenItems(p);
              const unread = noteUnread[p.id] || 0;
              const next = nextTerminOfProject(p, todayStr);
              const nextCat = next
                ? terminCategory({
                    project_type: p.project_type,
                    phase_key: next.key,
                  })
                : null;
              return (
                <button
                  key={p.id}
                  onClick={() => openProject(p.id)}
                  className="text-left"
                  data-testid={`project-card-${p.id}`}
                >
                  <Card className="border-border hover:border-primary/60 hover:shadow-sm transition-all h-full">
                    <CardContent className="p-4">
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {p.project_type === "pv" ? (
                            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                              <Sun className="h-4 w-4 text-primary" />
                            </div>
                          ) : (
                            <div className="h-8 w-8 rounded-md bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center shrink-0">
                              <Flame className="h-4 w-4 text-orange-500" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{p.kunde_name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {p.kunde_adresse || "—"}
                            </div>
                          </div>
                        </div>
                        {unread > 0 && (
                          <span
                            className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 min-w-[1.25rem] h-5 inline-flex items-center justify-center shrink-0"
                            data-testid={`project-card-${p.id}-unread`}
                          >
                            {unread}
                          </span>
                        )}
                      </div>

                      {/* Progress */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          <span>Fortschritt</span>
                          <span>{prog.done} / {prog.total}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${prog.pct}%` }}
                            data-testid={`project-card-${p.id}-progress`}
                          />
                        </div>
                      </div>

                      {/* Footer row */}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
                          {next && nextCat ? (
                            <>
                              <span className={`inline-block w-2 h-2 rounded-full ${CATEGORY_META[nextCat].color}`} />
                              <span className="truncate">
                                <span className="font-mono">{next.termin}</span> · {next.label}
                              </span>
                            </>
                          ) : (
                            <span className="italic text-muted-foreground">Kein Termin</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {openN > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 font-medium">
                              {openN} offen
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        )}

        {projects.length > 9 && (
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={() => navigate("/projekte")} data-testid="show-all-projects">
              Alle {projects.length} Projekte öffnen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
